import React, { FC } from 'react';
import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import WorkSansBold from 'assets/fonts/WorkSans-Bold.ttf';
import WorkSansMedium from 'assets/fonts/WorkSans-Medium.ttf';
import WorkSansRegular from 'assets/fonts/WorkSans-Regular.ttf';
import { TrafficLogVisitorType } from 'common/constans/common.constants';
import { visitorTypeMap } from 'common/constans/visitor.constants';
import { StrToDateTimeLocal } from 'common/helpers/date.helper';
import { ICitationRead } from 'common/services/citation-service/citation.service.types';
import { CitationStatus, citationStatusMap } from 'features/eiq-manage/enums/citation-status.enum';
import { formatSpeed, getCitationConsequencesText, getReportRegisteredTo } from '../helpers/citation-utils';
import { IPrintCitationListProps } from './print-citation-list.types';

Font.registerHyphenationCallback((word) => [word]);
Font.register({
  family: 'Work Sans',
  fonts: [
    { src: WorkSansRegular, fontWeight: 400 },
    { src: WorkSansBold, fontWeight: 600 },
    { src: WorkSansMedium, fontWeight: 500 },
  ],
});

interface CitationReportSummary {
  total: number;
  valid: number;
  confirmed: number;
  paid: number;
  warning: number;
}

const getReportSummary = (citations: ICitationRead[]): CitationReportSummary => {
  return citations.reduce<CitationReportSummary>(
    (summary, citation) => {
      summary.total += 1;

      if (citation.status !== CitationStatus.Pending && citation.status !== CitationStatus.Canceled) {
        summary.valid += 1;
      }
      if (citation.status === CitationStatus.PayNow || citation.status === CitationStatus.Delinquent) {
        summary.confirmed += 1;
      }
      if (citation.status === CitationStatus.Paid) {
        summary.paid += 1;
      }
      if (citation.status === CitationStatus.Warning) {
        summary.warning += 1;
      }

      return summary;
    },
    { total: 0, valid: 0, confirmed: 0, paid: 0, warning: 0 },
  );
};

const ReportHeader: FC<{ logo: string }> = ({ logo }) => (
  <View style={styles.header}>
    <Image src={logo} style={styles.logo} />
  </View>
);

const ReportFooter = () => (
  <View style={styles.footer} fixed>
    <Text>EIQ-IntelliTicket - Violations Report</Text>
    <Text render={({ pageNumber }) => `Page ${pageNumber}`} />
  </View>
);

const ListItemRow: FC<{ title: string; value?: string | number | null }> = ({ title, value }) => (
  <Text>
    <Text style={[styles.bold, styles.text]}>{title} </Text>
    <Text style={styles.text}>{value ?? ''}</Text>
  </Text>
);

const CitationListItem: FC<{ citation: ICitationRead }> = ({ citation }) => (
  <View style={styles.listItem} wrap={false}>
    <View style={styles.listItemColumn}>
      <ListItemRow title="Citation#:" value={citation.externalId} />
      <ListItemRow title="Date & Time:" value={StrToDateTimeLocal(citation.occurredAt)?.format('MM/DD/YYYY h:mmA')} />
      <ListItemRow title="License Plate:" value={citation.licensePlate} />
      <ListItemRow title="Registered to:" value={getReportRegisteredTo(citation)} />
      <ListItemRow title="Type:" value={visitorTypeMap[citation.ownerType as TrafficLogVisitorType]} />
    </View>
    <View style={styles.listItemColumn}>
      <ListItemRow title="Speed Limit:" value={formatSpeed(citation.speedLimit)} />
      <ListItemRow title="Recorded Speed:" value={formatSpeed(citation.violatingSpeed)} />
      <ListItemRow title="Consequences:" value={getCitationConsequencesText(citation)} />
      <ListItemRow title="Status:" value={citationStatusMap[citation.status as CitationStatus]} />
    </View>
  </View>
);

const PrintCitationList: FC<IPrintCitationListProps> = ({ citations, dateFrom, dateTo, logo }) => {
  const summary = getReportSummary(citations);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ReportHeader logo={logo} />
        <View>
          <Text style={[styles.title, styles.bold]}>
            Speed Violation Report: {dateFrom} - {dateTo}
          </Text>
        </View>
        <View style={styles.summary}>
          <Text style={[styles.bold, styles.text]}>Report Summary</Text>
          <Text style={styles.text}>{`Total Citations: ${summary.total}`}</Text>
          <Text style={styles.text}>{`Valid Citations (Excluding Pending/Canceled): ${summary.valid}`}</Text>
          <Text style={styles.text}>{`Confirmed: ${summary.confirmed}`}</Text>
          <Text style={styles.text}>{`Paid: ${summary.paid}`}</Text>
          <Text style={styles.text}>{`Warning: ${summary.warning}`}</Text>
          <Text style={styles.text}>{`Generated on: ${dayjs().format('MM/DD/YYYY hh:mm A')}`}</Text>
        </View>
        <View style={styles.listing}>
          <Text style={[styles.title, styles.bold]}>Citations List</Text>
        </View>
        {citations.map((citation) => (
          <CitationListItem key={citation.id} citation={citation} />
        ))}
        <ReportFooter />
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    position: 'absolute',
    bottom: 10,
    left: 20,
    right: 20,
    height: 20,
    fontSize: 10,
  },
  listItemColumn: { width: '50%' },
  listItem: { display: 'flex', flexDirection: 'row', marginBottom: 10, marginTop: 10, borderBottom: '1pt solid #292B2E', padding: 4 },
  listing: { marginTop: 20, marginBottom: 10 },
  summary: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 20 },
  text: { color: '#292B2E', fontSize: 12 },
  title: { color: '#292B2E', fontSize: 14 },
  bold: { fontWeight: 500 },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center',
    justifyContent: 'center',
    marginBottom: 30,
  },
  logo: {
    width: 120,
    marginTop: 20,
  },
  page: {
    paddingTop: 15,
    paddingHorizontal: 20,
    paddingBottom: 60,
    fontFamily: 'Work Sans',
    color: '#292B2E',
  },
});

export default PrintCitationList;
